import dayjs from "dayjs";

/** The dark mode vocabulary used internally and by the backend user settings. */
export type ThemeMode = "day" | "night" | "auto";

export const THEME_OVERRIDE_KEY = "themeModeOverride";

/**
 * Maps the vocabulary a client sends on the login redirect (`light|dark|auto`)
 * onto the one the app stores (`day|night|auto`).
 */
const URL_THEME_MAP: Record<string, ThemeMode> = {
  light: "day",
  dark: "night",
  auto: "auto",
};

const isThemeMode = (value: string | null): value is ThemeMode =>
  value === "day" || value === "night" || value === "auto";

/**
 * Resolves a theme mode to a dark mode boolean.
 *
 * Note "auto" is time of day based (dark between 21:00 and 05:59), not the
 * operating system's colour scheme preference.
 */
export const resolveIsDarkMode = (mode: string): boolean => {
  if (mode === "auto") {
    const currHour = dayjs().hour();
    return !(currHour >= 6 && currHour < 21);
  }
  return mode === "night";
};

/**
 * Reads a `theme=light|dark|auto` parameter off the current URL.
 *
 * Checks both the real query string and the hash query - the app uses a hash
 * router, so a client's login redirect lands on something like
 * `#/home?handover=true&userid=...&theme=light`.
 *
 * @returns The mapped theme mode, or null when absent or unrecognised.
 */
export const readThemeOverrideFromUrl = (): ThemeMode | null => {
  const sources = [window.location.search];

  const hash = window.location.hash;
  const queryIndex = hash.indexOf("?");
  if (queryIndex !== -1) sources.push(hash.slice(queryIndex + 1));

  for (const source of sources) {
    const value = new URLSearchParams(source).get("theme");
    if (value && URL_THEME_MAP[value.toLowerCase()]) {
      return URL_THEME_MAP[value.toLowerCase()];
    }
  }

  return null;
};

export const getThemeOverride = (): ThemeMode | null => {
  const stored = localStorage.getItem(THEME_OVERRIDE_KEY);
  return isThemeMode(stored) ? stored : null;
};

export const setThemeOverride = (mode: ThemeMode) => {
  localStorage.setItem(THEME_OVERRIDE_KEY, mode);
};

export const clearThemeOverride = () => {
  localStorage.removeItem(THEME_OVERRIDE_KEY);
};

/**
 * Picks up a `theme` parameter from the landing URL, persists it, and returns
 * the override now in force (falling back to a previously stored one).
 *
 * Must run before the router mounts: ProtectedRoute's handover redirect
 * replaces the URL and drops the query string. It is called while the
 * preferences slice builds its initial state, which is early enough - module
 * evaluation happens long before the first render.
 */
export const captureThemeOverrideFromUrl = (): ThemeMode | null => {
  const mode = readThemeOverrideFromUrl();
  if (mode) setThemeOverride(mode);
  return mode ?? getThemeOverride();
};
